import { User, Conversation, Message } from '../types';
import { APP_CONFIG } from '../constants/app';

/**
 * Servicio para manejar la lógica de conexiones entre usuarios
 * 
 * Proporciona funciones utilitarias para:
 * - Determinar si un swipe resulta en conexión
 * - Crear conversaciones y mensajes
 * - Gestionar participantes en conversaciones
 */
export class ConnectionService {
  /**
   * Determina si un swipe resulta en una conexión exitosa
   * Usa probabilidades configurables para simular el matching real
   * 
   * @param isSuper - Si es un super like (mayor probabilidad)
   * @returns true si hay conexión, false si no
   */
  static checkConnection(isSuper: boolean = false): boolean {
    const probability = isSuper 
      ? APP_CONFIG.CONNECTION_PROBABILITY.SUPER_LIKE 
      : APP_CONFIG.CONNECTION_PROBABILITY.NORMAL;
    
    return Math.random() < probability;
  }

  /**
   * Crea una nueva conversación entre dos usuarios
   * 
   * @param user1 - Primer usuario participante
   * @param user2 - Segundo usuario participante
   * @returns Nueva conversación con ID único
   */
  static createConversation(user1: User, user2: User): Conversation {
    return {
      id: `conv-${Date.now()}`,
      participants: [user1, user2],
      messages: [],
      updatedAt: new Date()
    };
  }

  /**
   * Crea un nuevo mensaje con timestamp actual
   * 
   * @param senderId - ID del usuario que envía
   * @param receiverId - ID del usuario que recibe
   * @param content - Contenido del mensaje
   * @returns Nuevo mensaje con ID único
   */
  static createMessage(
    senderId: string, 
    receiverId: string, 
    content: string
  ): Message {
    return {
      id: `msg-${Date.now()}`,
      senderId,
      receiverId,
      content,
      timestamp: new Date(),
      read: false
    };
  }

  /**
   * Obtiene el otro participante en una conversación
   * Útil para mostrar información del usuario con quien se chatea
   * 
   * @param conversation - La conversación
   * @param currentUserId - ID del usuario actual
   * @returns El otro usuario participante
   */
  static getOtherUser(conversation: Conversation, currentUserId: string): User {
    // Validar que conversation y participants existen
    if (!conversation || !conversation.participants || !Array.isArray(conversation.participants)) {
      console.error('❌ Conversación inválida:', conversation);
      throw new Error('Conversación inválida - no tiene participantes');
    }
    
    const otherUser = conversation.participants.find(p => p && p.id && p.id !== currentUserId);
    
    if (!otherUser) {
      console.error('❌ No se encontró el otro usuario en la conversación:', {
        conversationId: conversation.id,
        currentUserId,
        participants: conversation.participants
      });
      throw new Error('No se encontró el otro usuario en la conversación');
    }
    
    return otherUser;
  }

  /**
   * Verifica si una conversación tiene mensajes no leídos para un usuario
   * 
   * @param conversation - La conversación a verificar
   * @param userId - ID del usuario
   * @returns true si hay mensajes no leídos, false si no
   */
  static hasUnreadMessages(conversation: Conversation, userId: string): boolean {
    return conversation.messages.some(m => 
      m.receiverId === userId && !m.read
    );
  }
}